import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { portalColors, portalRadius } from './portalStyles';
import { JobHeroCard } from './JobHeroCard';
import { JobCompactCard } from './JobCompactCard';
import type { CrewPortalJob } from '../../types/crewPortal';

interface Props {
  monthJobs: CrewPortalJob[];
  accentColor: string;
  firstName: string;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatMonthYear(d: Date): string {
  return d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
}

function formatDayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function MonthView({ monthJobs, accentColor, firstName }: Props) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const todayStr = toDateStr(now);

  const [viewMonth, setViewMonth] = useState(currentMonth);
  const [viewYear, setViewYear] = useState(currentYear);
  const [selectedDay, setSelectedDay] = useState<string>(todayStr);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const isCurrentMonth = viewMonth === currentMonth && viewYear === currentYear;

  // Build date → jobs map
  const jobsByDate = useMemo(() => {
    const map = new Map<string, CrewPortalJob[]>();
    for (const j of monthJobs) {
      const arr = map.get(j.scheduled_date) ?? [];
      arr.push(j);
      map.set(j.scheduled_date, arr);
    }
    return map;
  }, [monthJobs]);

  // Calendar grid cells
  const calCells = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    // Monday = 0, Sunday = 6
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const cells: { day: number | null; dateStr: string }[] = [];
    for (let i = 0; i < startDay; i++) cells.push({ day: null, dateStr: '' });
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, dateStr: ds });
    }
    return cells;
  }, [viewMonth, viewYear]);

  // Stats
  const totalJobs = monthJobs.length;
  const locationSet = new Set(monthJobs.map(j => j.location?.name).filter(Boolean));

  // Selected day jobs
  const selectedJobs = jobsByDate.get(selectedDay) ?? [];

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  return (
    <div>
      {/* Month header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={20} color={portalColors.secondary} />
        </button>
        <span style={{ fontSize: 16, fontWeight: 500, color: portalColors.primary }}>
          {formatMonthYear(new Date(viewYear, viewMonth, 1))}
        </span>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronRight size={20} color={portalColors.secondary} />
        </button>
      </div>

      {/* Day-of-week header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {DAY_LABELS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, color: portalColors.muted, padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {calCells.map((cell, i) => {
          if (cell.day === null) return <div key={`empty-${i}`} />;
          const isToday = cell.dateStr === todayStr;
          const isSelected = cell.dateStr === selectedDay && !isToday;
          const dayJobs = jobsByDate.get(cell.dateStr) ?? [];
          const hasCompleted = dayJobs.some(j => j.status === 'completed');
          const hasJobs = dayJobs.length > 0;

          return (
            <div
              key={cell.dateStr}
              onClick={() => setSelectedDay(cell.dateStr)}
              style={{
                textAlign: 'center', padding: '8px 0', borderRadius: portalRadius.calDay,
                cursor: 'pointer', transition: 'background 150ms',
                background: isToday ? accentColor : isSelected ? `${accentColor}1A` : 'transparent',
              }}
              onMouseEnter={e => { if (!isToday && !isSelected) e.currentTarget.style.background = portalColors.subtleDivider; }}
              onMouseLeave={e => { if (!isToday && !isSelected) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                fontSize: 14, fontWeight: isToday || isSelected ? 500 : 400,
                color: isToday ? '#fff' : isSelected ? accentColor : portalColors.primary,
              }}>{cell.day}</div>
              {hasJobs && (
                <div style={{
                  width: 5, height: 5, borderRadius: '50%', margin: '3px auto 0',
                  background: isToday ? '#fff' : hasCompleted ? portalColors.completedDot : portalColors.upcomingDot,
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: portalColors.completedDot }} />
          <span style={{ fontSize: 11, color: portalColors.secondary }}>Completed</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: portalColors.upcomingDot }} />
          <span style={{ fontSize: 11, color: portalColors.secondary }}>Upcoming</span>
        </div>
      </div>

      {/* Month summary */}
      {isCurrentMonth && totalJobs > 0 && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: accentColor }}>
            {totalJobs} job{totalJobs !== 1 ? 's' : ''}{locationSet.size > 0 ? ` \u00B7 ${locationSet.size} ${locationSet.size === 1 ? 'location' : 'locations'}` : ''}
          </div>
          <div style={{ fontSize: 12, color: portalColors.muted, marginTop: 2 }}>
            {formatMonthYear(new Date(viewYear, viewMonth, 1))}
          </div>
        </div>
      )}

      {/* Selected day detail */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {selectedJobs.length === 0 && (
          <div style={{
            background: portalColors.card, borderRadius: portalRadius.button,
            borderLeft: `4px solid ${portalColors.divider}`, padding: 16,
            textAlign: 'center',
          }}>
            <span style={{ fontSize: 14, color: portalColors.secondary }}>
              No jobs on {formatDayLabel(selectedDay)}
            </span>
          </div>
        )}
        {selectedJobs.map(job => {
          const isPast = selectedDay < todayStr;
          const isToday = selectedDay === todayStr;
          return isToday ? (
            <JobHeroCard key={job.id} job={job} accentColor={accentColor} label="Today" firstName={firstName} />
          ) : (
            <JobCompactCard
              key={job.id}
              job={job}
              accentColor={accentColor}
              dateLabel={formatDayLabel(selectedDay)}
              isPast={isPast}
              isExpanded={expandedId === job.id}
              onExpand={() => setExpandedId(expandedId === job.id ? null : job.id)}
              firstName={firstName}
            />
          );
        })}
      </div>
    </div>
  );
}
